import type { CurrentCart, LanguageIsoCode } from '@/graphql/graphql';
import { ADD_TO_CART, GET_CART, GET_CART_SUMMARY, REMOVE_FROM_CART, UPDATE_CART } from '@/graphql/queries/cart.graphql';
import { apolloClient } from '@/lib/apollo-client';
import { ApolloError } from '@apollo/client';

export type CartSummary = {
    itemsCount: number;
};

type CartProgramParentWithFiles = NonNullable<CurrentCart['programs'][number]['program']['parent']> & {
    coverUrl?: string | null;
    files?: {
        coverUrl?: string | null;
    } | null;
};

type CatalogProgramWithFiles = CurrentCart['programs'][number]['program'] & {
    coverUrl?: string | null;
    files?: {
        coverUrl?: string | null;
    } | null;
    originalLanguage?: LanguageIsoCode | null;
    parent?: CartProgramParentWithFiles | null;
};

type CartProgramWithFiles = CurrentCart['programs'][number] & {
    program: CatalogProgramWithFiles;
};

type CartWithFiles = Omit<CurrentCart, 'programs'> & {
    programs: CartProgramWithFiles[];
};

const mapCartProgram = (cartProgram: CartProgramWithFiles): CurrentCart['programs'][number] => {
    const program = cartProgram.program;
    const parent: CatalogProgramWithFiles['parent'] = program.parent
        ? {
              ...program.parent,
              coverUrl: program.parent.files?.coverUrl ?? program.parent.coverUrl ?? null,
          }
        : program.parent;

    return {
        ...cartProgram,
        program: {
            ...program,
            coverUrl: program.files?.coverUrl ?? program.coverUrl ?? null,
            originalLanguage: program.production?.originalLanguage ?? program.originalLanguage ?? null,
            parent,
        } as CurrentCart['programs'][number]['program'],
    } as CurrentCart['programs'][number];
};

const mapCart = (cart: CartWithFiles): CurrentCart => ({
    ...cart,
    programs: cart.programs.map(mapCartProgram),
});

export const getCartSummary = async (): Promise<CartSummary> => {
    const response = await apolloClient.query({
        query: GET_CART_SUMMARY,
    });

    return {
        itemsCount: response.data.cart.quantity,
    };
};

export const getCart = async (): Promise<CurrentCart> => {
    const response = await apolloClient.query({
        query: GET_CART,
    });

    return mapCart(response.data.cart as CartWithFiles);
};

export const addToCart = async (
    programId: string,
    language: LanguageIsoCode,
    excludeEpisodeIds?: string[],
): Promise<CurrentCart> => {
    try {
        const { data } = await apolloClient.mutate({
            mutation: ADD_TO_CART,
            variables: {
                input: {
                    programId,
                    language,
                    ...(excludeEpisodeIds ? { excludeEpisodeIds } : {}),
                },
            },
        });

        if (!data) throw new Error('addToCart mutation returned no data');

        return mapCart(data.cart.billing.addToCart as CartWithFiles);
    } catch (error) {
        if (error instanceof ApolloError && error.graphQLErrors[0]?.message) {
            throw new Error(error.graphQLErrors[0].message);
        }
        if (error instanceof Error && error.message) {
            throw new Error(error.message);
        }
        throw new Error('Unable to add program to cart');
    }
};

export const removeFromCart = async (programId: string, language: LanguageIsoCode): Promise<CurrentCart> => {
    const { data } = await apolloClient.mutate({
        mutation: REMOVE_FROM_CART,
        variables: {
            input: { programId, language },
        },
    });

    if (!data) throw new Error('removeFromCart mutation returned no data');
    return mapCart(data.cart.billing.removeFromCart as CartWithFiles);
};

export const updateCart = async (
    programId: string,
    currentLanguage: LanguageIsoCode,
    newLanguage: LanguageIsoCode,
): Promise<CurrentCart> => {
    const { data } = await apolloClient.mutate({
        mutation: UPDATE_CART,
        variables: {
            input: {
                programId,
                currentLanguage,
                newLanguage,
            },
        },
    });

    if (!data) throw new Error('updateCart mutation returned no data');
    return mapCart(data.cart.billing.updateCart as CartWithFiles);
};
